import React, { useEffect, Fragment } from 'react';

import type { EditorActions } from '@atlaskit/editor-core';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

// The DO_NOT_USE here is an artifact of an old implementation. We can, in fact, use this
import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { usePublicLinkPageDataProvider } from '@confluence/public-links';
import { markErrorAsHandled } from '@confluence/graphql';
import {
	getPublishButtonDisabledReason,
	deleteDraft,
	useLabels,
	type PublishProps,
} from '@confluence/editor-features';
import type { RetrieveEditorTitleAndContent } from '@confluence/editor-features/entry-points/useRetrieveEditorTitleAndContent';
import { MainButtons } from '@confluence/editor-main-buttons';
import {
	EllipsisDropdownComponent,
	Buttons,
} from '@confluence/editor-ellipsis-dropdown/entry-points/EditorEllipsisDropdown';
import { LivePagesEllipsisDropdownComponent } from '@confluence/editor-ellipsis-dropdown/entry-points/LivePagesEllipsisDropdown';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { useNativeCollabConnected } from '@confluence/native-collab';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { EDITOR_CLOSE_BUTTON_CLICK, setViewTransitionSource } from '@confluence/browser-metrics';
import { withPageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container/entry-points/withPageTreeStateUpdater';
import type { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
import { useBlogTreeActions } from '@confluence/blog-tree-store/entry-points/useBlogTreeStore';
import { getParentOfDraft } from '@confluence/editor-features/entry-points/utils/getParentOfDraft';
import { useWillDeleteBlankDraftOnClose } from '@confluence/editor-features/entry-points/useWillDeleteBlankDraftOnClose';
import { useEmojiTitleCoverPictureAndContentState } from '@confluence/editor-features/entry-points/useEmojiTitleCoverPictureAndContentState';
import { useRestrictions } from '@confluence/editor-features/entry-points/useRestrictions';
import type { FlagsStateContainer } from '@confluence/flags';
import { fg } from '@confluence/feature-gating';

export type EditorPrimaryToolbarComponentsProps = {
	forceDisabled?: boolean;
	userOffline: boolean;
	collabLimitReached: boolean;
	contentId: string;
	contentType: string;
	isContentCreationPanelOpen: boolean;
	isUnpublishedDraft: boolean;
	onClickPublish: (arg: PublishProps) => () => void;
	onClickOpenContentCreationPanel: () => void;
	onPreviewClicked: (arg?: any) => void;
	saveDraft: (arg?: any) => Promise<void>;
	spaceKey: string;
	title: string | null;
	showRevertDialog: () => void;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	isPublishingContent: boolean;
	editorActions: EditorActions;
	isSingleUserSession: boolean;
	navigateOnClose: (args?: { isDiscarding?: boolean }) => void;
	triggerAnalyticsEvent: (arg: string, extraData?: { [key: string]: any }) => void;
	shouldRenderPreview: boolean;
	shouldShowRecentSpotlight: boolean;
	checkIfConditionPreventingPublish?: () => Promise<boolean>;
	flags: FlagsStateContainer;
	viewMode?: boolean;
	hasEditPermission?: boolean;
	pageTreeStateUpdater: PageTreeStateUpdater;
	ellipsisEditorActions?: {
		canUndo?: boolean;
		canRedo?: boolean;
		undo?: () => boolean;
		redo?: () => boolean;
		activateFindReplace?: () => boolean;
	};
};

const EditorPrimaryToolbarComponentsFunction = ({
	forceDisabled,
	userOffline,
	collabLimitReached,
	contentId,
	contentType,
	isContentCreationPanelOpen,
	isUnpublishedDraft,
	onClickPublish,
	onClickOpenContentCreationPanel,
	onPreviewClicked,
	saveDraft,
	spaceKey,
	title,
	showRevertDialog,
	retrieveEditorTitleAndContent,
	isPublishingContent,
	editorActions,
	isSingleUserSession,
	navigateOnClose,
	triggerAnalyticsEvent,
	shouldRenderPreview,
	shouldShowRecentSpotlight,
	flags,
	viewMode,
	pageTreeStateUpdater,
	hasEditPermission,
	ellipsisEditorActions,
}: EditorPrimaryToolbarComponentsProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		emojiCoverPictureAndContentStateSubscription,
		hasEmoji,
		hasCoverPicture,
		hasContentState,
	} = useEmojiTitleCoverPictureAndContentState();

	const { hasRestrictionsSubscription, hasRestrictions } = useRestrictions();
	const publicLinkData = usePublicLinkPageDataProvider({
		pageId: contentId,
		fetchPolicy: 'cache-and-network',
	});

	const hasLabels = useLabels(contentId);

	const [{ collabEditConnected }] = useNativeCollabConnected();

	const shouldDisableActions = isPublishingContent || userOffline || !!forceDisabled;
	const areMainButtonsDisabled = shouldDisableActions || publicLinkData.isLoading;

	const isPublishDisabledForPublishModal = fg('cc_publish_update_button_no_initial_disabled_state')
		? shouldDisableActions || collabLimitReached
		: areMainButtonsDisabled || collabLimitReached || !collabEditConnected;

	const hasTitle = Boolean(title);
	const hasBody = !editorActions.isDocumentEmpty();
	const { forceRefetchBlogTree } = useBlogTreeActions();

	const willDeleteBlankDraftOnClose = useWillDeleteBlankDraftOnClose({
		isUnpublishedDraft,
		isSingleUserSession,
		hasTitle,
		hasBody,
		hasEmoji,
		hasCoverPicture,
		hasContentState,
		hasRestrictions,
		hasLabels,
	});

	const { setOnboardingState } = useSetOnboardingState();
	// Data is not used so we don't worry about errors here
	useEffect(() => {
		for (const error of publicLinkData.errors) {
			markErrorAsHandled(error);
		}
	}, [publicLinkData.errors]);

	const publishButtonDisabledReason = getPublishButtonDisabledReason({
		title,
		isPublishingContent,
		userOffline,
		forceDisabled,
	});

	const onClose = async () => {
		if (contentType === 'page') {
			setViewTransitionSource(EDITOR_CLOSE_BUTTON_CLICK);
		}
		if (shouldShowRecentSpotlight) {
			void setOnboardingState({
				key: EDITOR_ONBOARDING_STATE_KEYS.RECENT_SPOTLIGHT_OPEN,
				value: 'true',
			});
		}

		if (willDeleteBlankDraftOnClose) {
			triggerAnalyticsEvent('close', {
				discarded: true,
			});
			void deleteDraft({
				contentId,
				deleteOnlyIfBlank: true,
			}).then(async ({ data }) => {
				if (contentType === 'blogpost') {
					// refreshes blog tree on discard close of blog draft
					forceRefetchBlogTree();
				}
				triggerAnalyticsEvent('draftDiscarded', {
					wasDeleted: !!data?.deleteContent?.wasDeleted,
				});
				const parentId = await getParentOfDraft(contentId);
				if (parentId) {
					pageTreeStateUpdater.setForceFetchPageId(parentId);
				} else {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'notFound',
							actionSubject: 'discardedDraftParent',
							objectType: contentType,
							objectId: contentId,
						},
					}).fire();
				}
			});
			navigateOnClose({ isDiscarding: true });
		} else {
			triggerAnalyticsEvent('close');
			void saveDraft({
				isOnClose: true,
			}).then(() => navigateOnClose());
		}
	};

	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);

	const allowedEllipsisButtons = !isCompanyHub
		? [
				Buttons.PUBLISH_WITHOUT_NOTIFY,
				Buttons.DELETE_DRAFT,
				Buttons.EDIT_LABELS,
				Buttons.MOVE_PAGE,
				Buttons.PREVIEW,
				Buttons.RESOLVED_COMMENTS,
				Buttons.TEMPLATE_BROWSER,
				Buttons.VIEW_CHANGES,
				Buttons.SWITCH_TO_LIVE_EDIT,
				Buttons.CLASSIFY_CONTENT,
			]
		: [Buttons.DELETE_DRAFT, Buttons.PREVIEW, Buttons.RESOLVED_COMMENTS, Buttons.VIEW_CHANGES];

	const isLivePage = useIsCurrentPageLive();
	const isEmbedded = isEmbeddedConfluence_DO_NOT_USE();
	const showEllipsisDropdown = !isEmbedded;

	const ellipsisDropdownProps = {
		contentId,
		contentType,
		isDisabled: areMainButtonsDisabled || userOffline || collabLimitReached,
		isUnpublishedDraft,
		isShowingLabelsDialog: false,
		onPreviewClicked,
		onClickPublish,
		retrieveEditorTitleAndContent,
		saveDraft,
		tooltipDisabledState: publishButtonDisabledReason,
		spaceKey,
		isContentCreationPanelShown: isContentCreationPanelOpen,
		onClickOpenContentCreationPanel,
		showRevertDialog,
		allowedEllipsisButtons,
		hasEditPermission,
		editorActions: ellipsisEditorActions,
	};

	// allowCopyLink and allowRestrictions are contingent on if user is not in Company hub space
	// Only inline comments button is whitelisted for Company hub editors to remain consistent with whitelisted actions in the EllipsisDropdown
	return (
		<Fragment>
			{emojiCoverPictureAndContentStateSubscription}
			{hasRestrictionsSubscription}
			<MainButtons
				allowWebItems
				allowCopyLink={!isCompanyHub && !isEmbedded}
				allowRestrictions={!isCompanyHub && !isEmbedded}
				allowStar={isLivePage && !isEmbedded}
				allowWatch={isLivePage && !isEmbedded}
				allowModeChanger={isLivePage && hasEditPermission && !fg('cc_live_pages_toggle_move')}
				areMainButtonsDisabled={areMainButtonsDisabled}
				contentId={contentId}
				contentType={contentType}
				isPublishDisabled={
					!title ||
					!title?.trim() ||
					areMainButtonsDisabled ||
					collabLimitReached ||
					!collabEditConnected
				}
				isCloseButtonDisabled={shouldDisableActions}
				isPublishDisabledForPublishModal={isPublishDisabledForPublishModal}
				isUnpublishedDraft={isUnpublishedDraft}
				discardOnCloseEnabled={willDeleteBlankDraftOnClose}
				onClose={onClose}
				onClickPreview={onPreviewClicked}
				onClickPublish={onClickPublish}
				publishButtonDisabledReason={publishButtonDisabledReason}
				isPublishingContent={isPublishingContent}
				retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
				shouldRenderPreview={shouldRenderPreview}
				flags={flags}
				saveDraft={saveDraft}
				title={title}
				subsequentNode={
					showEllipsisDropdown &&
					(isLivePage ? (
						<LivePagesEllipsisDropdownComponent
							contentId={contentId}
							spaceKey={spaceKey}
							retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
							onClickPublish={onClickPublish}
							saveDraft={saveDraft}
							isViewMode={viewMode && !hasEditPermission}
							onClickOpenContentCreationPanel={onClickOpenContentCreationPanel}
							isDisabled={areMainButtonsDisabled || userOffline || collabLimitReached}
						/>
					) : (
						//@ts-ignore
						<EllipsisDropdownComponent {...ellipsisDropdownProps} />
					))
				}
				spaceKey={spaceKey}
				editorActions={editorActions}
				viewMode={viewMode}
			/>
		</Fragment>
	);
};
export const EditorPrimaryToolbarComponents = withPageTreeStateUpdater(
	EditorPrimaryToolbarComponentsFunction,
);
